import React, { useReducer, useMemo } from "react";

import { css } from "@emotion/css";
import { QueryAwait } from "@steto-care/steto-query";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { GroupedInclusionPatient } from "@/models/GroupedInclusionModel.ts";
import type { InclusionAgentPatient } from "@/models/InclusionAgentModel.ts";
import type { NurseInclusionPatient } from "@/models/NurseInclusionPatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { CenteredLoader } from "@/uiKit/atoms/Loader.tsx";
import { SideSheet } from "@/uiKit/atoms/SideSheet.tsx";
import { HandledErrorCard } from "@/uiKit/organisms/HandledErrorCard.tsx";
import { CarePlanCreationForm } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationForm.tsx";
import { CarePlanCreationFormActions } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationFormActions.tsx";
import { InitialCarePlanCreationFormContent } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreationFormContent.tsx";
import {
  useCreateNoInsulinPatient,
  useCreatePatientAndCarePlan,
  useGeneratePrescription,
  useSignPrescriptionAndCreatePatient,
  type ExtraData,
} from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationAndRenewalActions.ts";
import { PatientCreationProvider } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationProvider.tsx";
import { patientCreationWizard } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationWizard.ts";
import type { PatientInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfo.ts";
import { PatientInfoForm } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfoForm.tsx";
import { PrescriptionInfoForm } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoForm.tsx";
import { PrescriptionInfoFormActions } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormActions.tsx";
import { PrescriptionInfoFormContent } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormContent.tsx";
import { PrescriptionInfoFormTitle } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormTitle.tsx";
import { PrescriptionSignActions } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignActions.tsx";
import { PrescriptionSignContent } from "@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignContent.tsx";

export type PatientCreationProps = {
  id: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (patientId: string) => void;
  defaultValues?: Partial<PatientInfo>;
  nurseInclusion?: NurseInclusionPatient;
  groupedInclusion?: GroupedInclusionPatient;
  inclusionAgent?: InclusionAgentPatient;
};

export const PatientCreation: React.FC<PatientCreationProps> = ({
  id,
  open,
  onClose,
  onSubmit,
  defaultValues,
  nurseInclusion,
  groupedInclusion,
  inclusionAgent,
}) => {
  const { t } = useTranslation();
  const extraData = useMemo(
    () => ({ groupedInclusion, inclusionAgent, nurseInclusion }),
    [groupedInclusion, inclusionAgent, nurseInclusion],
  );

  return (
    <SideSheet id={id} open={open} onClose={onClose}>
      <CardHeader title={t("pages.patients.addPatient")} variant="contained" />
      <QueryAwait errorFallback={HandledErrorCard} pendingFallback={CenteredLoader}>
        <PatientCreationContent
          onClose={onClose}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          extraData={extraData}
        />
      </QueryAwait>
    </SideSheet>
  );
};

export type PatientCreationContentProps = {
  onClose: () => void;
  onSubmit: (patientId: string) => void;
  defaultValues?: Partial<PatientInfo>;
  extraData?: ExtraData;
};

const PatientCreationContent: React.FC<PatientCreationContentProps> = ({
  defaultValues,
  onClose,
  onSubmit,
  extraData,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const [state, dispatch] = useReducer(patientCreationWizard.reducer, {
    step: "PatientInfo",
    PatientInfo: defaultValues ?? {},
  });
  const generatePrescription = useGeneratePrescription();
  const createNoInsulinPatient = useCreateNoInsulinPatient(
    createdPatientId => onSubmit(createdPatientId.id),
    extraData,
  );
  const patientCreationDirectives = Queries.practitioner.usePatientCreationDirectives({
    preinclusionId: extraData?.nurseInclusion?.id,
  });
  const { callback: createPatientAndCarePlan } = useCreatePatientAndCarePlan(
    createdPatientId => onSubmit(createdPatientId.id),
    undefined,
    extraData,
  );
  const { submit: signPrescriptionAndCreatePatient, isPending: isSignAndCreatePending } =
    useSignPrescriptionAndCreatePatient(createdPatientId => onSubmit(createdPatientId.id), extraData);

  return (
    <PatientCreationProvider patientCreationDirectives={patientCreationDirectives}>
      {state.step === "PatientInfo" && (
        <PatientInfoForm
          patientInfo={state.PatientInfo ?? {}}
          onSubmit={data =>
            dispatch({
              action: "TO_CarePlan",
              data,
            })
          }
          onBack={data => {
            dispatch({ action: "SET_DATA_PatientInfo", data });
            onClose();
          }}
          className={styles.form}
        />
      )}
      {state.step !== "PatientInfo" && (
        <CarePlanCreationForm
          carePlanInfo={state.CarePlan}
          onPrescription={data => dispatch({ action: "TO_PrescriptionInfo", data })}
          onCreateNoInsulin={data => createNoInsulinPatient(state.PatientInfo, data)}
          className={styles.form}
        >
          <InitialCarePlanCreationFormContent patientInfo={state.PatientInfo} />
          <CarePlanCreationFormActions
            onBack={data => dispatch({ action: "BACK_TO_PatientInfo", data })}
            className={styles.footer}
          />
        </CarePlanCreationForm>
      )}
      <Dialog
        id="prescription-dialog"
        open={state.step === "PrescriptionInfo" || state.step === "PrescriptionSign"}
        onClose={() => {
          if (state.step === "PrescriptionSign") {
            dispatch({ action: "BACK_TO_PrescriptionInfo" });
          }
          dispatch({ action: "BACK_TO_CarePlan" });
        }}
        maxWidth={state.step === "PrescriptionInfo" ? "xs" : "md"}
        classes={{ paper: styles.dialog }}
      >
        {state.step === "PrescriptionInfo" && (
          <PrescriptionInfoForm
            className={styles.dialogForm}
            prescriptionInfo={state.PrescriptionInfo ?? {}}
            onGeneratePrescription={data => {
              generatePrescription(
                {
                  patientInfo: state.PatientInfo,
                  carePlan: state.CarePlan,
                  prescriptionInfo: data,
                },
                ({ prescription }) =>
                  dispatch({
                    action: "TO_PrescriptionSign",
                    data: { ...data, prescription },
                  }),
              );
            }}
            onUploadPrescription={file =>
              createPatientAndCarePlan(state.PatientInfo, state.CarePlan, state.PrescriptionInfo ?? {}, file)
            }
          >
            <PrescriptionInfoFormTitle onExit={data => dispatch({ action: "BACK_TO_CarePlan", data })} />
            {state.step === "PrescriptionInfo" && (
              <PrescriptionInfoFormContent
                patientInfo={state.PatientInfo}
                carePlanStartDate={patientCreationDirectives.carePlanCreationDataDirectives.periodStart}
              />
            )}
            <PrescriptionInfoFormActions
              onBack={data => dispatch({ action: "BACK_TO_CarePlan", data })}
              submitTitle={t("diabetesForms.addPatient")}
              className={styles.footer}
            />
          </PrescriptionInfoForm>
        )}
        {state.step === "PrescriptionSign" && (
          <>
            <CardHeader
              id="prescription-sign-header"
              title={t("diabetesForms.prescriptionSignature")}
              variant="contained"
              onExit={() => {
                dispatch({
                  action: "BACK_TO_PrescriptionInfo",
                });
                dispatch({ action: "BACK_TO_CarePlan" });
              }}
            />
            <PrescriptionSignContent prescription={state.PrescriptionInfo.prescription} />
            <PrescriptionSignActions
              submitTitle={t("diabetesForms.signAndAddPatient")}
              onBack={() => {
                dispatch({
                  action: "BACK_TO_PrescriptionInfo",
                });
              }}
              onSubmit={() =>
                signPrescriptionAndCreatePatient(state.PatientInfo, state.CarePlan, state.PrescriptionInfo)
              }
              pending={isSignAndCreatePending}
              className={styles.footer}
            />
          </>
        )}
      </Dialog>
    </PatientCreationProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  form: css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "hidden",
  }),
  dialog: css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    alignItems: "center",
  }),
  dialogForm: css({
    width: "100%",
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
  }),
});
